import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  appointmentRequests as initialAppointmentRequests,
  documentRecords as initialDocumentRecords,
  exercises as initialExercises,
  pendingQuestionnaires as initialQuestionnaires,
  pendingSignatures as initialSignatures,
  pendingTestimonials as initialTestimonials,
  photoSessions as initialPhotoSessions,
  upcomingAppointments as initialUpcomingAppointments,
  type Appointment,
  type AppointmentRequest,
  type DocumentRecord,
  type ExerciseItem,
  type PendingQuestionnaire,
  type PendingTestimonial,
  type PhotoSession,
  type SignatureItem,
} from "../_data/mock";

interface DemoContextValue {
  documents: DocumentRecord[];
  signatures: SignatureItem[];
  appointmentRequests: AppointmentRequest[];
  upcomingAppointments: Appointment[];
  questionnaires: PendingQuestionnaire[];
  testimonials: PendingTestimonial[];
  exercises: ExerciseItem[];
  photoSessions: PhotoSession[];
  capturedShots: Record<string, string>;

  markDocumentReceived: (id: string) => void;
  signSignature: (id: string) => void;
  bookAppointmentRequest: (id: string) => void;
  cancelAppointment: (id: string) => void;
  rescheduleAppointment: (id: string, isoDate: string, time: string) => void;
  submitQuestionnaire: (id: string) => void;
  submitTestimonial: (id: string) => void;
  markExerciseDone: (id: string) => void;
  completePhotoSession: (id: string) => void;
  capturePhotoShot: (
    sessionId: string,
    shotId: string,
    dataUrl: string,
  ) => void;
}

const DemoCtx = createContext<DemoContextValue | null>(null);

export function DemoProvider({ children }: { children: ReactNode }) {
  const [documents, setDocuments] = useState<DocumentRecord[]>(
    initialDocumentRecords,
  );
  const [signatures, setSignatures] =
    useState<SignatureItem[]>(initialSignatures);
  const [appointmentRequests, setAppointmentRequests] = useState<
    AppointmentRequest[]
  >(initialAppointmentRequests);
  const [upcomingAppointments, setUpcomingAppointments] = useState<
    Appointment[]
  >(initialUpcomingAppointments);
  const [questionnaires, setQuestionnaires] = useState<PendingQuestionnaire[]>(
    initialQuestionnaires,
  );
  const [testimonials, setTestimonials] =
    useState<PendingTestimonial[]>(initialTestimonials);
  const [exercises, setExercises] = useState<ExerciseItem[]>(initialExercises);
  const [photoSessions, setPhotoSessions] =
    useState<PhotoSession[]>(initialPhotoSessions);
  const [capturedShots, setCapturedShots] = useState<Record<string, string>>(
    {},
  );

  const markDocumentReceived = useCallback((id: string) => {
    setDocuments((prev) =>
      prev.map((d) => (d.id === id ? { ...d, status: "received" } : d)),
    );
  }, []);

  const signSignature = useCallback((id: string) => {
    setSignatures((prev) => prev.filter((s) => s.id !== id));
  }, []);

  const bookAppointmentRequest = useCallback((id: string) => {
    setAppointmentRequests((prev) => prev.filter((r) => r.id !== id));
  }, []);

  const cancelAppointment = useCallback((id: string) => {
    setUpcomingAppointments((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const rescheduleAppointment = useCallback(
    (id: string, isoDate: string, time: string) => {
      setUpcomingAppointments((prev) =>
        prev.map((a) => (a.id === id ? { ...a, isoDate, time } : a)),
      );
    },
    [],
  );

  const submitQuestionnaire = useCallback((id: string) => {
    setQuestionnaires((prev) => prev.filter((q) => q.id !== id));
  }, []);

  const submitTestimonial = useCallback((id: string) => {
    setTestimonials((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const markExerciseDone = useCallback((id: string) => {
    setExercises((prev) =>
      prev.map((ex) => (ex.id === id ? { ...ex, doneToday: true } : ex)),
    );
  }, []);

  const completePhotoSession = useCallback((id: string) => {
    setPhotoSessions((prev) =>
      prev.map((p) =>
        p.id === id ? { ...p, status: "done", completedShots: 6 } : p,
      ),
    );
  }, []);

  const capturePhotoShot = useCallback(
    (sessionId: string, shotId: string, dataUrl: string) => {
      const key = `${sessionId}::${shotId}`;
      setCapturedShots((prev) => {
        const next = { ...prev, [key]: dataUrl };
        const countForSession = Object.keys(next).filter((k) =>
          k.startsWith(`${sessionId}::`),
        ).length;
        setPhotoSessions((sessions) =>
          sessions.map((s) =>
            s.id === sessionId
              ? {
                  ...s,
                  completedShots: countForSession,
                  // Reste in-progress même quand toutes les photos sont prises :
                  // c'est le bouton « Envoyer » qui finalise la séance.
                  status: countForSession > 0 ? "in-progress" : "todo",
                }
              : s,
          ),
        );
        return next;
      });
    },
    [],
  );

  const value = useMemo<DemoContextValue>(
    () => ({
      documents,
      signatures,
      appointmentRequests,
      upcomingAppointments,
      questionnaires,
      testimonials,
      exercises,
      photoSessions,
      capturedShots,
      markDocumentReceived,
      signSignature,
      bookAppointmentRequest,
      cancelAppointment,
      rescheduleAppointment,
      submitQuestionnaire,
      submitTestimonial,
      markExerciseDone,
      completePhotoSession,
      capturePhotoShot,
    }),
    [
      documents,
      signatures,
      appointmentRequests,
      upcomingAppointments,
      questionnaires,
      testimonials,
      exercises,
      photoSessions,
      capturedShots,
      markDocumentReceived,
      signSignature,
      bookAppointmentRequest,
      cancelAppointment,
      rescheduleAppointment,
      submitQuestionnaire,
      submitTestimonial,
      markExerciseDone,
      completePhotoSession,
      capturePhotoShot,
    ],
  );

  return <DemoCtx.Provider value={value}>{children}</DemoCtx.Provider>;
}

export function useDemo(): DemoContextValue {
  const ctx = useContext(DemoCtx);
  if (!ctx) throw new Error("useDemo must be used within DemoProvider");
  return ctx;
}
